import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';

import { SECTION } from 'src/constants/section';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import FilterDisplay from 'src/components/common/FilterDisplay';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import { orgUnitToFilter } from 'src/components/common/OrgTree/helper';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import PageFrame from 'src/components/common/PageFrame';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';
import OrgUnitHeader from 'src/components/planAction/OrgUnitHeader';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import { translateFilters } from 'src/components/planAction/PlanActionStatisticsPage/helpers';
import useGeneratePPTX from 'src/components/planAction/PlanActionStatisticsPage/hooks/useGeneratePPTX.ts';

import ActionPlanCoverage from './components/ActionPlanCoverage';
import FileTypeChooserModal from './components/FileTypeChooserModal';
import KPIStatus from './components/KPIStatus';
import OrgUnitStatus from './components/OrgUnitStatus';
import PPTModal from './components/PPTModal';
import Sidebar from './components/Sidebar';

import { styles } from './index.styles';

const Page = {
  OrgUnitStatus: 0,
  Coverage: 1,
  KPIStatus: 2,
};

const TabLabels = [
  { label: 'OrgUnitStatus', page: Page.OrgUnitStatus },
  { label: 'Coverage', page: Page.Coverage },
  { label: 'KPIInsights', page: Page.KPIStatus },
];

const PlanActionStatisticsPage = () => {
  const { t, i18n } = useTranslation('planAction');
  const { selectedOrgUnit } = usePlanActionContext();
  const { user } = useUserContext();
  const { getPermissionForModuleType } = useUserPermissions();
  const firstStatRef = useRef();
  const secondStatRef = useRef();
  const secondStatRefWrapper = useRef();
  const kpiStatusRef = useRef();
  const top5KpisRef = useRef();
  const [selectedKPI, setSelectedKPI] = useState(0);
  const [showFileTypeChooser, setShowFileTypeChooser] = useState(false);
  const [showPPTInfoModal, setShowPPTInfoModal] = useState(false);
  const [page, setPage] = useState(Page.OrgUnitStatus);
  const [statistics, setStatistics] = useState({ loading: true });
  const [isAllowedToExport, setIsAllowedToExport] = useState(false);
  const [extendedFilters, setExtendedFilters] = useState({});
  const { filterGroups, reset } = useFilterContext();
  const [filters, setFilters] = useState({
    function: undefined,
    level: undefined,
    priority: undefined,
  });
  const { poll } = useDownloadManager();

  // Reset filterContext on mount.
  useEffect(() => {
    reset(
      selectedOrgUnit
        ? {
            defaultFilterGroups: {
              [ORG_UNIT_FILTER_NAME]: [orgUnitToFilter(selectedOrgUnit)],
            },
            filterSpecs: [
              {
                name: 'orgUnit',
                multiple: false,
                required: true,
              },
              {
                name: new RegExp('.*'),
                multiple: true,
                required: false,
              },
            ],
          }
        : {},
    );
  }, []);

  const [pptGeneration, generatePPT] = useGeneratePPTX({
    firstStatRef,
    secondStatRef,
    kpiStatusRef,
    top5KpisRef,
    statistics,
    setShowPPTInfoModal,
    setSelectedKPI,
    page,
    setPage,
  });

  const handlePPTGenerate = () => {
    setShowPPTInfoModal(true);
    generatePPT(i18n.language);
  };

  const downloadCombinedStatistics = useCallback(
    () =>
      selectedOrgUnit &&
      HttpService.get(
        `${apiPaths.ORG_UNIT}${selectedOrgUnit.id}/export_tree/`,
        { lang: i18n.language },
        true,
      ).then(() => poll()),
    [selectedOrgUnit, poll],
  );

  useEffect(() => {
    if (!selectedOrgUnit)
      return history.replace(routePaths.PLAN_ACTION_OVERVIEW);

    // the currently selected org unit is part of the URL, not part of the query parameters, so we don't
    // need it in the filter values, as it is additionally recorded in the selectedOrgUnit variable
    const { orgUnit: _, ...restFilterGroups } = filterGroups;

    let flatFilters = {};
    Object.entries(restFilterGroups).forEach(([name, filterGroups]) => {
      if (!flatFilters[name]) {
        flatFilters[name] = [];
      }
      filterGroups.forEach((filterValue) => {
        flatFilters[name] = filterValue.filterExpr[name];
      });
    });

    async function getStatistics() {
      setStatistics(
        await HttpService.get(
          `${apiPaths.ORG_UNIT}${selectedOrgUnit.id}/plan_action_statistics/`,
          flatFilters,
          true,
        )
          .then(({ data }) => ({
            ...data,
            filtersSelected: !!flatFilters.length,
          }))
          .catch((error) => ({ error: `${error}` })),
      );
    }

    // ? This looks super awkward.
    // ? Error responses should not be treated as valid answer to determine user permissions.
    async function checkIsAllowedToExport() {
      // Check for all OrgUnits with includeSubnodes set to true on the current user,
      // if the current OrgUnit is a descendant of one of these OrgUnits.
      // If that's the case the statistics export button will be displayed.
      const ancestorCandidateIds = getPermissionForModuleType(
        SECTION.PLAN_ACTION,
      )
        ?.org_units.filter((org_unit) => !!org_unit.include_subnodes)
        ?.map((org_unit) => org_unit.id);

      setIsAllowedToExport(
        await HttpService.get(
          `${apiPaths.ORG_UNIT}${selectedOrgUnit.id}/is_descendant_of/`,
          { ancestor_candidates: ancestorCandidateIds },
        )
          .then(({ data }) => data['is_descendant_of'])
          .catch(() => false),
      );
    }

    setStatistics({ loading: true });

    void getStatistics();
    void checkIsAllowedToExport();
  }, [selectedOrgUnit, user, filterGroups, i18n.language]);

  useEffect(() => {
    if (pptGeneration && statistics.loading !== true) {
      const lang = i18n.language;
      void generatePPT(lang);
    }
  }, [statistics.loading]);

  useEffect(() => {
    HttpService.get(`${apiPaths.ORG_UNIT}org_unit_filters/`).then(({ data }) =>
      setExtendedFilters(data),
    );
  }, []);

  return (
    <PageFrame
      sidebar={
        <Sidebar
          filters={filters}
          setFilters={setFilters}
          extendedFilters={extendedFilters}
        />
      }
    >
      <FilterDisplay
        formatLabel={(name, values) => {
          const value = values[0];
          if (!value) return '';
          if (name === 'orgUnit') {
            return `${value.label}`;
          }
          const translatedText = translateFilters(value.filterExpr[name], t);
          return `${value.label}: ${translatedText || value.filterExpr[name]}`;
        }}
      />
      <OrgUnitHeader withHeader={false} />
      {statistics.loading ? (
        <Stack direction="row" sx={styles.overlay}>
          <CircularProgress />
        </Stack>
      ) : (
        <Box sx={styles.planActionStatistics} ref={secondStatRefWrapper}>
          <Box sx={styles.header}>
            <Box sx={styles.tabRow}>
              {TabLabels.map(({ label, page: value }) => (
                <Button
                  key={label}
                  onClick={() => setPage(value)}
                  data-test-id={`tab-${label}`}
                  sx={styles.tab(value === page)}
                >
                  {t(`Statistics.TabLabels.${label}`)}
                </Button>
              ))}
            </Box>

            {isAllowedToExport && (
              <Box sx={{ mr: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setShowFileTypeChooser(true)}
                  data-test-id="download-button"
                  disableRipple
                >
                  <DownloadRounded color="primary" />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={styles.tabContent}>
            {page === Page.OrgUnitStatus && (
              <OrgUnitStatus
                bigText={pptGeneration}
                cumulatedStatusCount={statistics.cumulatedStatusCount}
                passedRef={firstStatRef}
              />
            )}

            {page === Page.Coverage && (
              <ActionPlanCoverage
                bigText={pptGeneration}
                passedRef={secondStatRef}
                statistics={statistics}
              />
            )}

            {page === Page.KPIStatus && (
              <KPIStatus
                bigText={pptGeneration}
                kpis={statistics.kpiInsights}
                kpiStatusRef={kpiStatusRef}
                selectedKPI={selectedKPI}
                setSelectedKPI={setSelectedKPI}
                top5KpisRef={top5KpisRef}
              />
            )}
          </Box>
        </Box>
      )}
      {statistics.error && (
        <Box>
          <ErrorMessage>{statistics.error}</ErrorMessage>
        </Box>
      )}
      <FileTypeChooserModal
        onSelectFileType={downloadCombinedStatistics}
        onClose={() => setShowFileTypeChooser(false)}
        isOpen={showFileTypeChooser}
        headline={t('Statistics.CombinedReport')}
        note={t('Statistics.PPTExportNote')}
        handlePPTModal={() => handlePPTGenerate()}
      />
      <PPTModal isOpen={showPPTInfoModal} onClose={() => {}} />
    </PageFrame>
  );
};

export default PlanActionStatisticsPage;
